import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SettingsPage.css";
import "./PricingAdmin.css";
import { supabase } from "./supabaseClient";
import { dlog } from "./debugLog";
import {
  categoryNameUsedByPricing,
  defaultUnitForNewItem,
} from "./shared/pricing";
import {
  PRICING_ITEM_COLUMNS,
  checkCategoryName,
  describePatch,
  formatPrice,
  insertCategory,
  insertItem,
  parsePrice,
  renameCategory,
  rowPatch,
  setCategoryActive,
  updateItemFields,
  validatePatch,
  type ItemPatch,
  type PricingAdminItem,
} from "./shared/pricingAdminSave";

type PricingCategory = {
  id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at?: string | null;
};

type PricingItem = PricingAdminItem;

type RowStatus = { state: "saving" | "saved" | "error"; message?: string };
type UndoEntry = { id: string; before: ItemPatch; label: string };
type View = { kind: "cat"; id: number } | { kind: "uncat" };

const SAVED_BADGE_MS = 2500;

const isDesktopApp = () =>
  !!(window as any).estimator?.isDesktop ||
  navigator.userAgent.includes("Electron");

const prettyCat = (name: string) => (name || "").replace(/_/g, " ");

// Units are ea / lf / sf. Anything else is a code the pricing math reads
// (pricing.ts / App.tsx), so the page shows it but won't let it be changed.
const PLAIN_UNITS = ["ea", "lf", "sf"];
const SPECIAL_UNIT_HELP: Record<string, string> = {
  global_multiplier: "multiplies the whole estimate",
  decking_adjustment: "adds to the decking price per sf for a construction type",
  multiplier: "multiplies the price of the item it's attached to",
  addon_sf: "adds a per-sf amount on top of a base price",
  addon_lf: "adds a per-lf amount on top of a base price",
  bench_auto_lf: "bench price worked out from the decking chosen",
};
const unitKey = (u: string | null) => (u ?? "").toLowerCase().trim();
const isSpecialUnit = (u: string | null) =>
  !!unitKey(u) && !PLAIN_UNITS.includes(unitKey(u));
// Rows the pricing math finds by NAME ("Finance", "Perceived Value",
// "Deck less than…", construction-type adjustments).
const isNameLocked = (it: PricingItem) =>
  ["global_multiplier", "decking_adjustment"].includes(unitKey(it.unit));

const LockIcon: React.FC = () => (
  <svg className="pap-lockicon" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7.5" rx="1.5" fill="currentColor" />
    <path d="M5.2 7V5a2.8 2.8 0 0 1 5.6 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const bySort = (a: PricingItem, b: PricingItem) =>
  (a.sort_order ?? 999) - (b.sort_order ?? 999) ||
  (a.name || "").localeCompare(b.name || "");

const PricingAdmin: React.FC<{
  readOnly?: boolean;
  /** Called after every successful save, so the Estimator can refresh its
   *  prices. Also fires for a save that lands after the page has closed. */
  onSaved?: () => void;
}> = ({ readOnly = false, onSaved }) => {
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const notifySaved = () => onSavedRef.current?.();
  dlog("PricingAdmin readOnly =", readOnly);
  const [categories, setCategories] = useState<PricingCategory[]>([]);
  const [items, setItems] = useState<PricingItem[]>([]);
  const [view, setView] = useState<View | null>(null);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  // "+ Add item": a draft shown in the panel. Nothing is written until the
  // user clicks Add item, so an abandoned form never leaves a junk row.
  const [newDraft, setNewDraft] = useState<PricingItem | null>(null);
  const [newErr, setNewErr] = useState<string | null>(null);
  const unitTouchedRef = useRef(false);
  // Categories: add (modal), rename (inline), hide/show. Each saves at once;
  // catStatus shows Saving… / Saved ✓ / Couldn't save – Retry by the title.
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatErr, setNewCatErr] = useState<string | null>(null);
  const [addingCat, setAddingCat] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameText, setRenameText] = useState("");
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [catStatus, setCatStatus] = useState<Record<number, RowStatus>>({});
  const catRetryRef = useRef<Record<number, () => void>>({});
  // Enter finishes a rename and the box then loses focus — finish once.
  const renameOpenRef = useRef(false);

  // A just-added item lands at the end of its list — scroll it into view.
  const [scrollToId, setScrollToId] = useState<string | null>(null);
  useEffect(() => {
    if (!scrollToId) return;
    const el = document.querySelector(`[data-item-id="${scrollToId}"]`);
    if (el && typeof (el as any).scrollIntoView === "function") {
      (el as HTMLElement).scrollIntoView({ block: "center", behavior: "smooth" });
    }
    setScrollToId(null);
  }, [scrollToId]);

  // What's typed in a price box while it has focus ("1,40" mid-edit), so the
  // box isn't reformatted under the cursor. Cleared when the box loses focus.
  const [priceText, setPriceText] = useState<Record<string, string>>({});

  // =========================
  // Autosave state
  // =========================
  // Each row saves by itself when the user leaves a field (or presses
  // Enter). savedRef holds the values the database last confirmed per row;
  // the difference between that and the row on screen is what gets sent.
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
  const [undo, setUndo] = useState<UndoEntry | null>(null);
  const itemsRef = useRef<PricingItem[]>([]);
  const savedRef = useRef<Record<string, PricingItem>>({});
  // Rows added on this page that aren't in the database yet (temp ids).
  const newIdsRef = useRef<Set<string>>(new Set());
  // temp id -> real id once the insert has come back.
  const aliasRef = useRef<Record<string, string>>({});
  // One save at a time per row, in the order they were asked for.
  const chainRef = useRef<Record<string, Promise<void>>>({});
  const savedTimersRef = useRef<Record<string, number>>({});

  const denyIfReadOnly = () => {
    setError("Read-only access. Ask an admin to make changes.");
  };

  // =========================
  // Data loading
  // =========================
  const loadAll = async () => {
    setLoading(true);
    setError(null);

    const [catRes, itemRes] = await Promise.all([
      supabase
        .from("pricing_categories")
        .select("id, name, sort_order, is_active, created_at")
        .order("name", { ascending: true }),
      supabase
        .from("pricing_items2")
        .select(PRICING_ITEM_COLUMNS)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true }),
    ]);

    if (catRes.error) {
      setError(catRes.error.message);
      setLoading(false);
      return;
    }
    if (itemRes.error) {
      setError(itemRes.error.message);
      setLoading(false);
      return;
    }

    const cleanedCats: PricingCategory[] =
      catRes.data?.map((c: any) => ({ ...c, is_active: !!c.is_active })) ?? [];

    const cleanedItems: PricingItem[] =
      itemRes.data?.map((r: any) => ({
        ...r,
        cost: r.cost == null ? null : Number(r.cost),
        active: !!r.active,
        deleted_at: r.deleted_at ?? null,
        category_id: r.category_id == null ? null : Number(r.category_id),
      })) ?? [];

    setCategories(cleanedCats);
    setItemsSynced(cleanedItems);
    savedRef.current = Object.fromEntries(
      cleanedItems.map((it) => [it.id, it])
    );
    newIdsRef.current = new Set();
    setRowStatus({});
    setUndo(null);
    setPriceText({});

    setView((prev) => {
      if (prev?.kind === "uncat") return prev;
      if (prev?.kind === "cat" && cleanedCats.some((c) => c.id === prev.id)) {
        return prev;
      }
      const first = cleanedCats.find((c) => c.is_active);
      return first ? { kind: "cat", id: Number(first.id) } : null;
    });

    setLoading(false);
  };

  useEffect(() => {
    document.body.classList.add("pa-wide");
    loadAll();
    const timers = savedTimersRef.current;

    return () => {
      document.body.classList.remove("pa-wide");
      // Leaving the page (e.g. switching to Estimator) without the focused
      // field losing focus first: save whatever is still on screen.
      flushUnsaved();
      Object.values(timers).forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // See src/App.tsx — Electron silently cancels close when a renderer
    // sets preventDefault on beforeunload, breaking the X button. Skip the
    // guard on desktop; web still gets the native unsaved-changes prompt.
    if (isDesktopApp()) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsaved()) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Autosave engine
  // =========================
  const setItemsSynced = (next: PricingItem[]) => {
    itemsRef.current = next;
    setItems(next);
  };

  const resolveId = (id: string) => aliasRef.current[id] ?? id;

  const setStatus = (id: string, s: RowStatus | null) => {
    window.clearTimeout(savedTimersRef.current[id]);
    setRowStatus((prev) => {
      const next = { ...prev };
      if (s) next[id] = s;
      else delete next[id];
      return next;
    });
    if (s?.state === "saved") {
      // "Saved" shows briefly, then the row goes quiet again.
      savedTimersRef.current[id] = window.setTimeout(() => {
        setRowStatus((prev) => {
          if (prev[id]?.state !== "saved") return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, SAVED_BADGE_MS);
    }
  };

  const updateItem = (id: string, field: keyof PricingItem, value: any) => {
    if (readOnly) {
      denyIfReadOnly();
      return;
    }
    const rid = resolveId(id);
    setItemsSynced(
      itemsRef.current.map((it) =>
        it.id === rid ? { ...it, [field]: value } : it
      )
    );
  };

  /** Put one field back to its last-saved value (Escape key). */
  const revertField = (id: string, field: keyof PricingItem) => {
    const rid = resolveId(id);
    const saved = savedRef.current[rid];
    if (!saved) return;
    updateItem(rid, field, saved[field]);
  };

  const doSave = async (rawId: string, isUndo: boolean) => {
    const id = resolveId(rawId);
    const cur = itemsRef.current.find((it) => it.id === id);
    if (!cur) return;

    // A row added on this page: the first save creates it.
    if (newIdsRef.current.has(id)) {
      const invalid = validatePatch({
        name: cur.name,
        cost: cur.cost,
        sort_order: cur.sort_order,
      });
      if (invalid) {
        setStatus(id, { state: "error", message: invalid });
        return;
      }
      setStatus(id, { state: "saving" });
      const res = await insertItem(supabase, cur);
      if ("error" in res) {
        setStatus(id, { state: "error", message: res.error });
        return;
      }
      const realId = res.row.id;
      newIdsRef.current.delete(id);
      aliasRef.current[id] = realId;
      chainRef.current[realId] = chainRef.current[id];
      savedRef.current[realId] = res.row;
      // Keep anything typed while the insert was on its way.
      setItemsSynced(
        itemsRef.current.map((it) => (it.id === id ? { ...it, id: realId } : it))
      );
      setStatus(id, null);
      setStatus(realId, { state: "saved" });
      notifySaved();
      return;
    }

    const saved = savedRef.current[id];
    if (!saved) return;
    const patch = rowPatch(saved, cur);
    const fields = Object.keys(patch) as (keyof ItemPatch)[];
    if (fields.length === 0) {
      // Nothing changed (or it was changed back) — clear an old error.
      setRowStatus((prev) => {
        if (prev[id]?.state !== "error") return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    const invalid = validatePatch(patch);
    if (invalid) {
      setStatus(id, { state: "error", message: invalid });
      return;
    }

    setStatus(id, { state: "saving" });
    const res = await updateItemFields(supabase, id, patch);
    if ("error" in res) {
      setStatus(id, { state: "error", message: res.error });
      return;
    }
    savedRef.current[id] = res.row;
    if (isUndo) {
      setUndo(null);
    } else {
      const before: ItemPatch = {};
      fields.forEach((f) => ((before as any)[f] = (saved as any)[f]));
      setUndo({ id, before, label: describePatch(patch, res.row.name) });
    }
    setStatus(id, { state: "saved" });
    notifySaved();
  };

  const saveRow = (id: string, isUndo = false): Promise<void> => {
    if (readOnly) return Promise.resolve();
    const key = resolveId(id);
    const prev = chainRef.current[key] ?? Promise.resolve();
    const next = prev.then(() => doSave(id, isUndo));
    chainRef.current[key] = next.catch(() => {});
    return next;
  };

  const waitForSaves = () =>
    Promise.all(Object.values(chainRef.current)).then(() => {});

  /** Rows whose screen values aren't in the database yet. */
  const unsavedRows = () =>
    itemsRef.current.filter((it) => {
      if (newIdsRef.current.has(it.id)) return true;
      const saved = savedRef.current[it.id];
      return !!saved && Object.keys(rowPatch(saved, it)).length > 0;
    });

  const hasUnsaved = () => unsavedRows().length > 0;

  const flushUnsaved = () => {
    unsavedRows().forEach((it) => {
      saveRow(it.id);
    });
  };

  const handleUndo = () => {
    if (!undo) return;
    const id = resolveId(undo.id);
    setItemsSynced(
      itemsRef.current.map((it) => (it.id === id ? { ...it, ...undo.before } : it))
    );
    setUndo(null);
    saveRow(id, true);
  };

  const handleRefresh = async () => {
    await waitForSaves();
    const pending = unsavedRows().length;
    if (pending > 0) {
      const ok = window.confirm(
        `${pending} change${pending === 1 ? "" : "s"} couldn't be saved (marked in red). Refresh anyway and lose ${pending === 1 ? "it" : "them"}?`
      );
      if (!ok) return;
    }
    loadAll();
  };

  const restoreItem = (id: string) => {
    updateItem(id, "deleted_at", null);
    saveRow(id);
  };

  /** Toggles, dropdowns, Trash: change and save at once. */
  const changeNow = (id: string, patch: ItemPatch) => {
    if (readOnly) {
      denyIfReadOnly();
      return;
    }
    const rid = resolveId(id);
    setItemsSynced(
      itemsRef.current.map((it) => (it.id === rid ? { ...it, ...patch } : it))
    );
    saveRow(rid);
  };

  const trashItem = (id: string) => {
    if (!window.confirm("Move this item to Trash?")) return;
    changeNow(id, { deleted_at: new Date().toISOString() });
    setDetailId(null);
  };

  const moveToCategory = (id: string, catId: number) => {
    const cat = categories.find((c) => Number(c.id) === catId);
    if (!cat) return;
    changeNow(id, { category: cat.name, category_id: Number(cat.id) });
  };

  // =========================
  // + Add item
  // =========================
  const draftDefaults = (cat: PricingCategory) => {
    const inCat = itemsRef.current.filter(
      (it) => Number(it.category_id) === Number(cat.id)
    );
    return {
      category: cat.name,
      category_id: Number(cat.id),
      sort_order: inCat.reduce((m, it) => Math.max(m, it.sort_order ?? 0), 0) + 1,
      unit: defaultUnitForNewItem(cat.name, inCat),
    };
  };

  const startAddItem = () => {
    if (readOnly) {
      denyIfReadOnly();
      return;
    }
    if (!currentCat) return;
    unitTouchedRef.current = false;
    setNewErr(null);
    setDetailId(null);
    setNewDraft({
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: "",
      cost: null,
      active: true,
      proposal_description: "",
      deleted_at: null,
      ...draftDefaults(currentCat),
    });
  };

  const editDraft = (patch: Partial<PricingItem>) => {
    if ("unit" in patch) unitTouchedRef.current = true;
    setNewDraft((d) => (d ? { ...d, ...patch } : d));
    setNewErr(null);
  };

  const draftCategory = (catId: number) => {
    const cat = categories.find((c) => Number(c.id) === catId);
    if (!cat) return;
    const defs = draftDefaults(cat);
    setNewDraft((d) =>
      d
        ? {
            ...d,
            ...defs,
            // Keep a unit the user picked; otherwise follow the category.
            unit: unitTouchedRef.current ? d.unit : defs.unit,
          }
        : d
    );
  };

  const createItem = () => {
    if (!newDraft) return;
    const name = (newDraft.name || "").trim();
    if (!name) return setNewErr("Give the item a name.");
    if (newDraft.cost == null || !Number.isFinite(newDraft.cost)) {
      return setNewErr("Enter a price (0 is fine).");
    }
    const dupe = itemsRef.current.some(
      (it) =>
        !it.deleted_at &&
        Number(it.category_id) === Number(newDraft.category_id) &&
        (it.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (dupe) {
      return setNewErr(`There's already an item called “${name}” in this category.`);
    }
    const row = { ...newDraft, name };
    newIdsRef.current.add(row.id);
    setItemsSynced([...itemsRef.current, row]);
    setNewDraft(null);
    setNewErr(null);
    // Show the list the new item went into.
    setView({ kind: "cat", id: Number(row.category_id) });
    setSearch("");
    setShowTrash(false);
    setScrollToId(row.id);
    saveRow(row.id);
  };

  // =========================
  // Categories
  // =========================
  const byName = (a: PricingCategory, b: PricingCategory) =>
    a.name.localeCompare(b.name);

  const setCatSaveStatus = (id: number, st: RowStatus | null) => {
    setCatStatus((prev) => {
      const next = { ...prev };
      if (st) next[id] = st;
      else delete next[id];
      return next;
    });
    if (st?.state === "saved") {
      window.setTimeout(
        () =>
          setCatStatus((prev) => {
            if (prev[id]?.state !== "saved") return prev;
            const next = { ...prev };
            delete next[id];
            return next;
          }),
        SAVED_BADGE_MS
      );
    }
  };

  /** Run one category save with status + Retry. */
  const runCatSave = async (
    id: number,
    op: () => Promise<{ cat: PricingCategory } | { error: string }>,
    onSaved: (cat: PricingCategory) => void
  ) => {
    const attempt = async () => {
      setCatSaveStatus(id, { state: "saving" });
      const res = await op();
      if ("error" in res) {
        setCatSaveStatus(id, { state: "error", message: res.error });
        return;
      }
      delete catRetryRef.current[id];
      setCategories((prev) =>
        prev.map((c) => (Number(c.id) === id ? { ...c, ...res.cat } : c)).sort(byName)
      );
      onSaved(res.cat);
      setCatSaveStatus(id, { state: "saved" });
      notifySaved();
    };
    catRetryRef.current[id] = attempt;
    await attempt();
  };

  const openAddCategory = () => {
    if (readOnly) {
      denyIfReadOnly();
      return;
    }
    setNewCatName("");
    setNewCatErr(null);
    setAddCatOpen(true);
  };

  const addCategory = async () => {
    const problem = checkCategoryName(
      newCatName,
      categories.map((c) => ({ id: Number(c.id), name: c.name })),
      categoryNameUsedByPricing
    );
    if (problem) return setNewCatErr(problem);
    setAddingCat(true);
    const res = await insertCategory(supabase, newCatName);
    setAddingCat(false);
    if ("error" in res) return setNewCatErr(res.error);
    setCategories((prev) => [...prev, res.cat].sort(byName));
    notifySaved();
    setAddCatOpen(false);
    openView({ kind: "cat", id: res.cat.id });
    setCatSaveStatus(res.cat.id, { state: "saved" });
  };

  const startRename = (cat: PricingCategory) => {
    setRenameText(prettyCat(cat.name));
    setRenameErr(null);
    setRenaming(true);
    renameOpenRef.current = true;
  };

  const finishRename = (cat: PricingCategory) => {
    if (!renameOpenRef.current) return;
    const text = renameText.trim();
    if (!text || text === cat.name || text === prettyCat(cat.name)) {
      renameOpenRef.current = false;
      setRenaming(false);
      return;
    }
    const id = Number(cat.id);
    const problem = checkCategoryName(
      text,
      categories.map((c) => ({ id: Number(c.id), name: c.name })),
      categoryNameUsedByPricing,
      id
    );
    if (problem) return setRenameErr(problem);
    renameOpenRef.current = false;
    setRenaming(false);
    runCatSave(
      id,
      () => renameCategory(supabase, id, text),
      (saved) => {
        // The items were renamed in the database too — mirror that here so
        // their next save doesn't see a category difference.
        const rename = (it: PricingItem) =>
          Number(it.category_id) === id ? { ...it, category: saved.name } : it;
        setItemsSynced(itemsRef.current.map(rename));
        Object.keys(savedRef.current).forEach((k) => {
          savedRef.current[k] = rename(savedRef.current[k]);
        });
      }
    );
  };

  const toggleCategory = (cat: PricingCategory) => {
    if (readOnly) {
      denyIfReadOnly();
      return;
    }
    const hide = cat.is_active;
    if (
      hide &&
      !window.confirm(
        `Hide “${prettyCat(cat.name)}”? It disappears from the Estimator's Add Item list. Its items and prices are kept, and saved estimates don't change.`
      )
    ) {
      return;
    }
    const id = Number(cat.id);
    runCatSave(id, () => setCategoryActive(supabase, id, !hide), () => {});
  };

  /** Move up / down within its category. Many rows share a sort number (all
   *  of Demo is 0), so the list is renumbered 1..n in its current order with
   *  the two rows swapped; only rows whose number actually changes are saved.
   *  No Undo for a move — moving it back is one click. */
  const moveItem = (id: string, dir: -1 | 1) => {
    if (readOnly) return;
    const rid = resolveId(id);
    const me = itemsRef.current.find((it) => it.id === rid);
    if (!me) return;
    const sameGroup = (it: PricingItem) =>
      !it.deleted_at &&
      (me.category_id == null
        ? it.category_id == null
        : Number(it.category_id) === Number(me.category_id));
    const order = itemsRef.current.filter(sameGroup).sort(bySort).map((it) => it.id);
    const idx = order.indexOf(rid);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    const nextSort = new Map(order.map((oid, i) => [oid, i + 1]));
    const changed = itemsRef.current.filter(
      (it) => nextSort.has(it.id) && it.sort_order !== nextSort.get(it.id)
    );
    setItemsSynced(
      itemsRef.current.map((it) =>
        nextSort.has(it.id) ? { ...it, sort_order: nextSort.get(it.id)! } : it
      )
    );
    changed.forEach((it) => saveRow(it.id, true));
  };

  // =========================
  // Price boxes
  // =========================
  const priceProps = (it: PricingItem) => ({
    value: priceText[it.id] ?? formatPrice(it.cost),
    disabled: readOnly,
    inputMode: "decimal" as const,
    "aria-label": `Price for ${it.name}`,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setPriceText((prev) => ({ ...prev, [it.id]: text }));
      updateItem(it.id, "cost", parsePrice(text));
    },
    onBlur: () => {
      setPriceText((prev) => {
        const next = { ...prev };
        delete next[it.id];
        return next;
      });
      saveRow(it.id);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        (e.target as HTMLElement).blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        revertField(it.id, "cost");
        const saved = savedRef.current[resolveId(it.id)];
        setPriceText((prev) => ({ ...prev, [it.id]: formatPrice(saved?.cost) }));
      }
    },
  });

  // =========================
  // Derived lists
  // =========================
  const catById = useMemo(
    () => new Map(categories.map((c) => [Number(c.id), c])),
    [categories]
  );
  const activeCats = categories.filter((c) => c.is_active);
  const inactiveCats = categories.filter((c) => !c.is_active);
  const isUncat = (it: PricingItem) =>
    it.category_id == null || !catById.has(Number(it.category_id));
  const uncatCount = items.filter((it) => isUncat(it) && !it.deleted_at).length;

  const countByCat = useMemo(() => {
    const m = new Map<number, number>();
    items.forEach((it) => {
      if (it.deleted_at || it.category_id == null) return;
      const k = Number(it.category_id);
      m.set(k, (m.get(k) ?? 0) + 1);
    });
    return m;
  }, [items]);

  const currentCat =
    view?.kind === "cat" ? catById.get(view.id) ?? null : null;
  const inView = (it: PricingItem) =>
    view?.kind === "uncat"
      ? isUncat(it)
      : view?.kind === "cat"
      ? Number(it.category_id) === view.id
      : false;
  const viewItems = items.filter((it) => inView(it) && !it.deleted_at).sort(bySort);
  const trashItems = items.filter((it) => inView(it) && !!it.deleted_at).sort(bySort);

  const q = search.trim().toLowerCase();
  const searchHits = q
    ? items
        .filter(
          (it) =>
            !it.deleted_at &&
            ((it.name || "").toLowerCase().includes(q) ||
              (it.proposal_description || "").toLowerCase().includes(q))
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    : [];

  const detail = detailId
    ? items.find((it) => it.id === resolveId(detailId)) ?? null
    : null;

  const savingCount = Object.values(rowStatus).filter(
    (s) => s.state === "saving"
  ).length;
  const failedRows = items.filter((it) => rowStatus[it.id]?.state === "error");

  const openView = (v: View) => {
    renameOpenRef.current = false;
    setRenaming(false);
    setView(v);
    setSearch("");
    setShowTrash(false);
  };

  // =========================
  // Render helpers
  // =========================
  const catLabel = (it: PricingItem) =>
    isUncat(it) ? "Uncategorized" : prettyCat(catById.get(Number(it.category_id))!.name);

  const itemRow = (it: PricingItem, showCat = false) => (
    <div
      key={it.id}
      className={[
        "pap-row",
        !it.active ? "is-hidden" : "",
        detailId === it.id ? "is-open" : "",
        rowStatus[it.id]?.state === "error" ? "is-err" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-item-id={it.id}
    >
      <button
        type="button"
        className="pap-row-main"
        title="Edit details"
        onClick={() => setDetailId(it.id)}
      >
        <span className="pap-row-name">
          {it.name}
          {!it.active && <span className="pap-chip">Hidden</span>}
          {showCat && <span className="pap-chip soft">{catLabel(it)}</span>}
        </span>
        {it.proposal_description ? (
          <span className="pap-row-desc">{it.proposal_description}</span>
        ) : null}
      </button>
      <div className="pap-row-price">
        <input className="ui-input pap-price" {...priceProps(it)} />
        <span className={`pap-unit${isSpecialUnit(it.unit) ? " special" : ""}`}>
          {isSpecialUnit(it.unit) ? <LockIcon /> : "/"} {it.unit || "—"}
        </span>
      </div>
      <div className="pap-row-status">
        <RowSaveStatus status={rowStatus[it.id]} onRetry={() => saveRow(it.id)} />
      </div>
    </div>
  );

  const catButton = (c: PricingCategory, dim = false) => (
    <button
      key={c.id}
      type="button"
      className={[
        "pap-cat",
        dim ? "is-dim" : "",
        !q && view?.kind === "cat" && view.id === Number(c.id) ? "is-on" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => openView({ kind: "cat", id: Number(c.id) })}
    >
      <span>
        {prettyCat(c.name)}
        {categoryNameUsedByPricing(c.name) && (
          <span className="pap-lock" title="Used by the pricing math — can't be renamed or hidden">
            <LockIcon />
          </span>
        )}
      </span>
      <span className="pap-count">{countByCat.get(Number(c.id)) ?? 0}</span>
    </button>
  );

  const listHeader = (title: string, sub: string, right?: React.ReactNode) => (
    <div className="card-head">
      <div style={{ minWidth: 0 }}>
        <div className="pap-h2">{title}</div>
        <div className="card-sub">{sub}</div>
      </div>
      {right && <div className="card-right">{right}</div>}
    </div>
  );

  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  const categoryHeader = (cat: PricingCategory, right: React.ReactNode) => {
    const id = Number(cat.id);
    const lockReason = categoryNameUsedByPricing(cat.name);
    const st = catStatus[id];
    return (
      <div className="card-head">
        <div style={{ minWidth: 0 }}>
          {renaming ? (
            <>
              <input
                className="ui-input pap-catname"
                aria-label="Category name"
                autoFocus
                value={renameText}
                onChange={(e) => {
                  setRenameText(e.target.value);
                  setRenameErr(null);
                }}
                onBlur={() => finishRename(cat)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    finishRename(cat);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    renameOpenRef.current = false;
                    setRenaming(false);
                  }
                }}
              />
              {renameErr && <div className="pap-err">{renameErr}</div>}
            </>
          ) : (
            <div className="pap-h2">
              {prettyCat(cat.name)}
              {!cat.is_active && <span className="pap-chip">Hidden</span>}
              {st?.state === "saving" && <span className="pap-st busy">Saving…</span>}
              {st?.state === "saved" && <span className="pap-st ok">Saved ✓</span>}
              {st?.state === "error" && (
                <span className="pap-st err" title={st.message}>
                  Couldn't save –{" "}
                  <button
                    type="button"
                    className="pap-link"
                    onClick={() => catRetryRef.current[id]?.()}
                  >
                    Retry
                  </button>
                </span>
              )}
            </div>
          )}
          <div className="card-sub">
            {showTrash
              ? `Trash · ${plural(trashItems.length, "item")}`
              : plural(viewItems.length, "item")}
            {!cat.is_active ? " · Hidden from the Estimator." : ""}
            {lockReason && (
              <>
                {" · "}
                <LockIcon /> {lockReason} It can't be renamed or hidden.
              </>
            )}
            {st?.state === "error" && st.message ? ` · ${st.message}` : ""}
          </div>
        </div>
        <div className="card-right">{right}</div>
      </div>
    );
  };

  return (
    <div className="pap-page">
      {readOnly && (
        <div className="pap-banner" role="note">
          <strong>Read-only.</strong> Ask an admin to change prices.
        </div>
      )}

      {error && <div className="pap-banner err">{error}</div>}

      {failedRows.length > 0 && (
        <div className="pap-banner err" role="alert">
          {failedRows.length === 1
            ? "1 change couldn't be saved:"
            : `${failedRows.length} changes couldn't be saved:`}{" "}
          {failedRows
            .map(
              (it) =>
                `${(it.name || "item").trim() || "item"} — ${
                  rowStatus[it.id]?.message || "save failed"
                }`
            )
            .join("; ")}
          <button
            type="button"
            className="ui-btn ghost pap-small"
            onClick={() => failedRows.forEach((it) => saveRow(it.id))}
          >
            Retry
          </button>
        </div>
      )}

      <div className="pap-top">
        <div>
          <div className="settings-kicker">Pricing</div>
          <div className="pap-h1">Price list</div>
        </div>
        <div className="pap-top-right">
          {!readOnly && (
            <span
              className={`pap-savestate${
                failedRows.length ? " err" : savingCount ? " busy" : ""
              }`}
              role="status"
              aria-live="polite"
            >
              {failedRows.length
                ? `${failedRows.length} couldn't save`
                : savingCount
                ? "Saving…"
                : "Changes save automatically"}
            </span>
          )}
          {undo && !readOnly && (
            <button
              type="button"
              className="ui-btn ghost pap-undo"
              onClick={handleUndo}
              title={`Undo ${undo.label}`}
            >
              ↶ Undo {undo.label}
            </button>
          )}
          <button
            type="button"
            className="ui-btn ghost"
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </button>
          <input
            className="ui-input pap-search"
            placeholder="Search all items…"
            aria-label="Search all items"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="pap-grid">
        {/* ---------------- Categories ---------------- */}
        <aside className="card pap-cats">
          <div className="card-head">
            <div className="card-title">Categories</div>
            {!readOnly && (
              <button
                type="button"
                className="ui-btn ghost pap-small"
                onClick={openAddCategory}
              >
                + Add category
              </button>
            )}
          </div>
          <nav className="pap-catlist" aria-label="Categories">
            {activeCats.map((c) => catButton(c))}

            {(inactiveCats.length > 0 || uncatCount > 0) && (
              <>
                <div className="pap-catgroup">Inactive / Uncategorized</div>
                {inactiveCats.map((c) => catButton(c, true))}
                {uncatCount > 0 && (
                  <button
                    type="button"
                    className={`pap-cat is-dim${
                      !q && view?.kind === "uncat" ? " is-on" : ""
                    }`}
                    onClick={() => openView({ kind: "uncat" })}
                  >
                    <span>Uncategorized</span>
                    <span className="pap-count">{uncatCount}</span>
                  </button>
                )}
              </>
            )}
          </nav>
        </aside>

        {/* ---------------- Items ---------------- */}
        <section className="card pap-items">
          {loading ? (
            <div className="pap-empty">Loading pricing…</div>
          ) : q ? (
            <>
              {listHeader(
                "Search",
                `${plural(searchHits.length, "item")} matching “${search.trim()}” across all categories`,
                <button
                  type="button"
                  className="ui-btn ghost pap-small"
                  onClick={() => setSearch("")}
                >
                  Clear
                </button>
              )}
              <div className="pap-list">
                {searchHits.length === 0 ? (
                  <div className="pap-empty">No items match.</div>
                ) : (
                  searchHits.map((it) => itemRow(it, true))
                )}
              </div>
            </>
          ) : view?.kind === "uncat" ? (
            <>
              {listHeader(
                "Uncategorized",
                "These items have no category, so the Estimator can't offer them."
              )}
              <div className="pap-list">{viewItems.map((it) => itemRow(it))}</div>
            </>
          ) : currentCat ? (
            <>
              {categoryHeader(
                currentCat,
                <>
                  {!categoryNameUsedByPricing(currentCat.name) && !readOnly && !showTrash && (
                    <>
                      <button
                        type="button"
                        className="ui-btn ghost pap-small"
                        onClick={() => startRename(currentCat)}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="ui-btn ghost pap-small"
                        onClick={() => toggleCategory(currentCat)}
                      >
                        {currentCat.is_active ? "Hide category" : "Show category"}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={`ui-btn ghost pap-small${showTrash ? " is-on" : ""}`}
                    onClick={() => setShowTrash((v) => !v)}
                  >
                    {showTrash ? "Back to items" : `Trash (${trashItems.length})`}
                  </button>
                  {!showTrash && !readOnly && (
                    <button
                      type="button"
                      className="ui-btn pap-primary pap-small"
                      onClick={startAddItem}
                    >
                      + Add item
                    </button>
                  )}
                </>
              )}
              <div className="pap-list">
                {showTrash ? (
                  trashItems.length === 0 ? (
                    <div className="pap-empty">Trash is empty.</div>
                  ) : (
                    trashItems.map((it) => (
                      <div key={it.id} className="pap-row is-trash" data-item-id={it.id}>
                        <button
                          type="button"
                          className="pap-row-main"
                          title="Edit details"
                          onClick={() => setDetailId(it.id)}
                        >
                          <span className="pap-row-name">{it.name}</span>
                        </button>
                        <div className="pap-row-price">
                          <span className="pap-trash-price">{formatPrice(it.cost)}</span>
                          <span className="pap-unit">/ {it.unit || "—"}</span>
                        </div>
                        <div className="pap-row-status">
                          <RowSaveStatus
                            status={rowStatus[it.id]}
                            onRetry={() => saveRow(it.id)}
                          />
                          <button
                            type="button"
                            className="ui-btn ghost pap-small"
                            disabled={readOnly}
                            onClick={() => restoreItem(it.id)}
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    ))
                  )
                ) : viewItems.length === 0 ? (
                  <div className="pap-empty">
                    No items in this category yet.{" "}
                    {!readOnly && (
                      <button type="button" className="pap-link" onClick={startAddItem}>
                        + Add item
                      </button>
                    )}
                  </div>
                ) : (
                  viewItems.map((it) => itemRow(it))
                )}
              </div>
            </>
          ) : (
            <div className="pap-empty">Pick a category.</div>
          )}
        </section>
      </div>

      {addCatOpen && (
        <div className="pap-modal-back" onMouseDown={() => setAddCatOpen(false)}>
          <div
            className="card pap-modal"
            role="dialog"
            aria-label="Add category"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="card-head">
              <div className="card-title">Add category</div>
            </div>
            <div className="card-body">
              <label className="field">
                <span className="micro">Name</span>
                <input
                  className="ui-input"
                  aria-label="New category name"
                  autoFocus
                  placeholder="e.g. Outdoor Kitchen"
                  value={newCatName}
                  onChange={(e) => {
                    setNewCatName(e.target.value);
                    setNewCatErr(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addCategory();
                    if (e.key === "Escape") setAddCatOpen(false);
                  }}
                />
              </label>
              <div className="micro mt12">
                It shows up in the Estimator's Add Item list. You can rename or
                hide it later.
              </div>
              {newCatErr && <div className="pap-err mt12">{newCatErr}</div>}
              <div className="pap-modal-actions">
                <button
                  type="button"
                  className="ui-btn ghost"
                  onClick={() => setAddCatOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ui-btn pap-primary"
                  disabled={addingCat}
                  onClick={addCategory}
                >
                  {addingCat ? "Adding…" : "Add category"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {newDraft && (
        <ItemPanel
          key={newDraft.id}
          isNew
          item={newDraft}
          categories={categories}
          readOnly={readOnly}
          status={null}
          newErr={newErr}
          onClose={() => {
            setNewDraft(null);
            setNewErr(null);
          }}
          onCreate={createItem}
          onEdit={(field, value) => editDraft({ [field]: value })}
          onLeave={() => {}}
          onRevert={() => {}}
          savedCost={null}
          onNow={(patch) => editDraft(patch)}
          onCategory={draftCategory}
          onMove={() => {}}
          onTrash={() => {}}
          onRestore={() => {}}
        />
      )}

      {!newDraft && detail && (
        <ItemPanel
          key={detail.id}
          item={detail}
          categories={categories}
          readOnly={readOnly}
          status={
            <RowSaveStatus
              status={rowStatus[detail.id]}
              onRetry={() => saveRow(detail.id)}
            />
          }
          onClose={() => setDetailId(null)}
          onEdit={(field, value) => updateItem(detail.id, field, value)}
          onLeave={() => saveRow(detail.id)}
          onRevert={(field) => revertField(detail.id, field)}
          savedCost={savedRef.current[detail.id]?.cost ?? null}
          onNow={(patch) => changeNow(detail.id, patch)}
          onCategory={(catId) => moveToCategory(detail.id, catId)}
          onMove={(dir) => moveItem(detail.id, dir)}
          onTrash={() => trashItem(detail.id)}
          onRestore={() => restoreItem(detail.id)}
        />
      )}
    </div>
  );
};

// ==================================================================
// Item detail panel (slides in from the right). Text fields save when
// they lose focus; dropdowns, the checkbox, moves and Trash save at once.
// ==================================================================
const ItemPanel: React.FC<{
  item: PricingItem;
  /** "+ Add item" mode: nothing saves until Add item is clicked. */
  isNew?: boolean;
  newErr?: string | null;
  onCreate?: () => void;
  categories: PricingCategory[];
  readOnly: boolean;
  status: React.ReactNode;
  savedCost: number | null;
  onClose: () => void;
  onEdit: (field: keyof PricingItem, value: any) => void;
  onLeave: () => void;
  onRevert: (field: keyof PricingItem) => void;
  onNow: (patch: ItemPatch) => void;
  onCategory: (catId: number) => void;
  onMove: (dir: -1 | 1) => void;
  onTrash: () => void;
  onRestore: () => void;
}> = (p) => {
  const it = p.item;
  const special = isSpecialUnit(it.unit);
  const nameLocked = !p.isNew && isNameLocked(it);
  const trashed = !!it.deleted_at;
  const [priceText, setPriceText] = useState<string | null>(null);

  // Escape closes the panel when no field is being edited.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== "Escape" || (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      p.onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textKeys = (field: keyof PricingItem, multiline = false) => ({
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !multiline) {
        e.preventDefault();
        (e.target as HTMLElement).blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        p.onRevert(field);
        if (field === "cost") setPriceText(formatPrice(p.savedCost));
      }
    },
  });

  const catIdNum = it.category_id == null ? null : Number(it.category_id);
  const knownCat = p.categories.some((c) => Number(c.id) === catIdNum);
  const activeCats = p.categories.filter((c) => c.is_active);
  const hiddenCats = p.categories.filter((c) => !c.is_active);

  return (
    <div className="pap-panel-back" onMouseDown={p.onClose}>
      <aside
        className="pap-panel"
        role="dialog"
        aria-label={p.isNew ? "Add item" : `Edit ${it.name}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="pap-panel-head">
          <div style={{ minWidth: 0 }}>
            <div className="settings-kicker">
              {p.isNew ? "New item" : trashed ? "Item · in Trash" : "Item"}
            </div>
            <div className="pap-h2">{p.isNew ? "Add item" : it.name || "Untitled"}</div>
          </div>
          <div className="pap-panel-head-right">
            {p.status}
            <button type="button" className="ui-btn ghost pap-small" onClick={p.onClose}>
              {p.isNew ? "Cancel" : "Done"}
            </button>
          </div>
        </div>

        <div className="pap-panel-body">
          <label className="field">
            <span className="micro">Name</span>
            <input
              className="ui-input"
              aria-label="Name"
              autoFocus={p.isNew}
              placeholder={p.isNew ? "e.g. Trex Transcend Railing" : undefined}
              value={it.name ?? ""}
              disabled={p.readOnly || nameLocked}
              onChange={(e) => p.onEdit("name", e.target.value)}
              onBlur={p.onLeave}
              {...textKeys("name")}
            />
            {nameLocked && (
              <span className="pap-warn">
                <LockIcon /> The pricing math finds this item by its name, so it
                can't be renamed.
              </span>
            )}
          </label>

          <div className="pap-two">
            <label className="field">
              <span className="micro">Price</span>
              <input
                className="ui-input"
                aria-label="Price"
                inputMode="decimal"
                placeholder={p.isNew ? "0" : undefined}
                value={priceText ?? formatPrice(it.cost)}
                disabled={p.readOnly}
                onChange={(e) => {
                  setPriceText(e.target.value);
                  p.onEdit("cost", parsePrice(e.target.value));
                }}
                onBlur={() => {
                  setPriceText(null);
                  p.onLeave();
                }}
                {...textKeys("cost")}
              />
            </label>

            <label className="field">
              <span className="micro">Unit</span>
              {special ? (
                <div className="ui-input pap-locked" aria-label="Unit (locked)">
                  <LockIcon /> {unitKey(it.unit)}
                </div>
              ) : (
                <select
                  className="ui-input"
                  aria-label="Unit"
                  value={unitKey(it.unit) || "ea"}
                  disabled={p.readOnly}
                  onChange={(e) => p.onNow({ unit: e.target.value })}
                >
                  <option value="ea">ea — each</option>
                  <option value="lf">lf — linear foot</option>
                  <option value="sf">sf — square foot</option>
                </select>
              )}
            </label>
          </div>

          {special && (
            <div className="pap-warn block">
              <strong>Pricing code, not a unit.</strong> “{unitKey(it.unit)}”{" "}
              {SPECIAL_UNIT_HELP[unitKey(it.unit)] || "is read by the pricing math"}.
              Changing it would change how estimates are priced, so it's locked
              here.
            </div>
          )}

          <label className="field">
            <span className="micro">Category</span>
            <select
              className="ui-input"
              aria-label="Category"
              value={knownCat ? String(catIdNum) : ""}
              disabled={p.readOnly}
              onChange={(e) => e.target.value && p.onCategory(Number(e.target.value))}
            >
              {!knownCat && <option value="">Choose a category…</option>}
              {activeCats.map((c) => (
                <option key={c.id} value={c.id}>
                  {prettyCat(c.name)}
                </option>
              ))}
              {hiddenCats.length > 0 && (
                <optgroup label="Hidden categories">
                  {hiddenCats.map((c) => (
                    <option key={c.id} value={c.id}>
                      {prettyCat(c.name)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {!p.isNew && (
              <span className="micro">
                Moving it changes which Estimator list it shows up in. Saved
                estimates keep their prices.
              </span>
            )}
          </label>

          <label className="field">
            <span className="micro">Proposal description</span>
            <textarea
              className="ui-textarea"
              aria-label="Proposal description"
              rows={5}
              value={it.proposal_description ?? ""}
              disabled={p.readOnly}
              placeholder="What the customer reads on the proposal"
              onChange={(e) => p.onEdit("proposal_description", e.target.value)}
              onBlur={p.onLeave}
              {...textKeys("proposal_description", true)}
            />
          </label>

          <label className="pap-toggle">
            <input
              type="checkbox"
              aria-label="Show in Estimator"
              checked={!!it.active}
              disabled={p.readOnly}
              onChange={(e) => p.onNow({ active: e.target.checked })}
            />
            <span>
              <strong>Show in Estimator</strong>
              <span className="micro"> — untick to hide it without deleting</span>
            </span>
          </label>

          {!trashed && !p.isNew && (
            <div className="pap-moves">
              <span className="micro">Position in list</span>
              <button
                type="button"
                className="ui-btn ghost pap-small"
                disabled={p.readOnly}
                onClick={() => p.onMove(-1)}
              >
                ↑ Move up
              </button>
              <button
                type="button"
                className="ui-btn ghost pap-small"
                disabled={p.readOnly}
                onClick={() => p.onMove(1)}
              >
                ↓ Move down
              </button>
            </div>
          )}
        </div>

        {p.isNew && p.newErr && (
          <div className="pap-err pap-panel-err" role="alert">
            {p.newErr}
          </div>
        )}

        <div className="pap-panel-foot">
          {p.isNew ? (
            <>
              <span className="micro">Nothing is saved until you click Add item.</span>
              <button type="button" className="ui-btn pap-primary" onClick={p.onCreate}>
                Add item
              </button>
            </>
          ) : (
            <span className="micro">Changes save when you leave a field.</span>
          )}
          {p.isNew ? null : trashed ? (
            <button
              type="button"
              className="ui-btn ghost pap-small"
              disabled={p.readOnly}
              onClick={p.onRestore}
            >
              Restore
            </button>
          ) : (
            <button
              type="button"
              className="ui-btn ghost pap-danger"
              disabled={p.readOnly}
              onClick={p.onTrash}
            >
              Move to Trash
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};

/** Saving… / Saved ✓ / Couldn't save – Retry, shown at the end of each row. */
const RowSaveStatus: React.FC<{
  status?: RowStatus;
  onRetry: () => void;
}> = ({ status, onRetry }) => {
  if (status?.state === "saving") {
    return <span className="pap-st busy">Saving…</span>;
  }
  if (status?.state === "saved") {
    return <span className="pap-st ok">Saved ✓</span>;
  }
  if (status?.state === "error") {
    return (
      <span className="pap-st err" title={status.message}>
        Couldn't save –{" "}
        <button type="button" className="pap-link" onClick={onRetry}>
          Retry
        </button>
      </span>
    );
  }
  return null;
};

export default PricingAdmin;